/** JSON body on failed POST /api/public/ask-page/{handle}/questions. */
export type AskPageAskErrorBody = {
	message?: string;
	reason?: string;
	errors?: Record<string, string[]>;
};

export function parseAskPageAskError(
	error: unknown,
): AskPageAskErrorBody | undefined {
	if (typeof error !== "object" || error === null) {
		return undefined;
	}
	const body = error as Record<string, unknown>;
	const errors = body.errors;
	return {
		message: typeof body.message === "string" ? body.message : undefined,
		reason: typeof body.reason === "string" ? body.reason : undefined,
		errors:
			errors !== null && typeof errors === "object" && !Array.isArray(errors)
				? Object.fromEntries(
						Object.entries(errors as Record<string, unknown>).flatMap(
							([field, messages]) => {
								if (!Array.isArray(messages)) {
									return [];
								}
								const strings = messages.filter(
									(m): m is string => typeof m === "string",
								);
								return strings.length > 0 ? [[field, strings]] : [];
							},
						),
					)
				: undefined,
	};
}

/** First validation message for a field, when Laravel returns 422 errors. */
export function askPageValidationMessage(
	error: AskPageAskErrorBody | undefined,
	field: string,
): string | undefined {
	return error?.errors?.[field]?.[0];
}

/**
 * 402 is shared by creator entitlement (pass/subscription), exhausted question
 * credits, and an account that cannot be billed. Only the first should stash
 * for a post-purchase resubmit.
 */
export function askPage402UserMessage(error: AskPageAskErrorBody | undefined): {
	message: string;
	stashForEntitlementPurchase: boolean;
} {
	if (error?.reason === "billing_account_missing") {
		return {
			message:
				error.message ??
				"Your account isn't set up to pay for questions yet. Please contact support.",
			stashForEntitlementPurchase: false,
		};
	}

	if (
		error?.reason === "insufficient_credits" ||
		error?.message?.toLowerCase().includes("no credits left")
	) {
		return {
			message:
				error.message ??
				"You have no credits left. Wait for your allowance to reset or add credits to your plan to continue.",
			stashForEntitlementPurchase: false,
		};
	}

	return {
		message:
			error?.message ??
			"You need a pass or subscription to ask this creator a question.",
		stashForEntitlementPurchase: true,
	};
}
